import { useEffect, useRef, useState } from 'react'

import { Bandpass, Biquad } from '../../lib/headband/signalQuality'
import type { SamplesEvent } from '../../lib/headband/types'

/**
 * Live traces, one row per channel, drawn on a canvas at animation-frame rate
 * from a ring buffer that the headband fills directly — no React state in the
 * sample path. The raw signal carries 50–400 mV of DC, so the display filter is
 * on by default: 1–40 Hz, or a 1 Hz high-pass alone when switched off. Auto
 * scale fits the window; the fixed scales are the operator's.
 */
export type Scale = 'auto' | number

export interface SampleBus {
  subscribe(handler: (e: SamplesEvent) => void): () => void
}

const SCALES: [Scale, string][] = [
  ['auto', 'Auto'],
  [50, '±50 µV'],
  [100, '±100'],
  [200, '±200'],
  [500, '±500'],
  [1000, '±1 mV'],
  [2000, '±2 mV'],
]
const WINDOWS = [3, 5, 10]
const FS = 250
const MAX_SECONDS = 10

export function LiveSignal({ channels, bus }: { channels: readonly string[]; bus: SampleBus }) {
  const [scale, setScale] = useState<Scale>('auto')
  const [windowSeconds, setWindowSeconds] = useState(5)
  const [bandpass, setBandpass] = useState(true)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  // The draw loop reads the controls through a ref so a change never restarts
  // the ring buffer; the ref is synced in an effect, never during render.
  const settings = useRef({ scale, windowSeconds, bandpass })
  useEffect(() => {
    settings.current = { scale, windowSeconds, bandpass }
  }, [scale, windowSeconds, bandpass])

  useEffect(() => {
    const n = channels.length
    const cap = FS * MAX_SECONDS
    const rings = channels.map(() => new Float32Array(cap))
    let head = 0
    let filled = 0
    let filterOn = settings.current.bandpass
    let filters = channels.map(() => (filterOn ? new Bandpass(FS) : Biquad.highpass(FS, 1)))
    const unsubscribe = bus.subscribe((e) => {
      if (settings.current.bandpass !== filterOn) {
        filterOn = settings.current.bandpass
        filters = channels.map(() => (filterOn ? new Bandpass(FS) : Biquad.highpass(FS, 1)))
      }
      for (const s of e.samples) {
        for (let c = 0; c < n; c++) rings[c]![head] = filters[c]!.step(s.values[c] ?? 0)
        head = (head + 1) % cap
        if (filled < cap) filled++
      }
    })

    let raf = 0
    const draw = () => {
      raf = requestAnimationFrame(draw)
      const canvas = canvasRef.current
      if (!canvas) return
      const dpr = window.devicePixelRatio || 1
      const cssW = canvas.clientWidth || 600
      const rowH = 64
      const cssH = rowH * n
      if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
        canvas.width = Math.round(cssW * dpr)
        canvas.height = Math.round(cssH * dpr)
      }
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, cssW, cssH)
      const styles = getComputedStyle(canvas)
      const line = styles.getPropertyValue('--line').trim() || '#333'
      const ink = styles.getPropertyValue('--on-surface-cyan').trim() || '#4fd1ff'
      const muted = styles.getPropertyValue('--muted').trim() || '#888'
      const win = Math.min(cap, settings.current.windowSeconds * FS)
      const count = Math.min(win, filled)
      const labelW = 60
      const plotW = cssW - labelW - 8
      ctx.font = '12px ui-monospace, SFMono-Regular, Menlo, monospace'
      for (let c = 0; c < n; c++) {
        const top = c * rowH
        ctx.strokeStyle = line
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(0, top + rowH - 0.5)
        ctx.lineTo(cssW, top + rowH - 0.5)
        ctx.stroke()
        ctx.fillStyle = muted
        ctx.fillText(channels[c]!, 8, top + rowH / 2 + 4)
        if (count < 2) continue
        const ring = rings[c]!
        const start = (head - count + cap) % cap
        let peak = 0
        let sum = 0
        for (let i = 0; i < count; i++) {
          const v = ring[(start + i) % cap]!
          sum += v
          peak = Math.max(peak, Math.abs(v))
        }
        const mean = sum / count
        const s = settings.current.scale
        const amp = s === 'auto' ? Math.max(20, peak * 1.1) : s
        ctx.fillText(s === 'auto' ? `±${Math.round(amp)}` : `±${amp}`, labelW + plotW - 44, top + 14)
        ctx.strokeStyle = ink
        ctx.lineWidth = 1.25
        ctx.beginPath()
        for (let i = 0; i < count; i++) {
          const v = ring[(start + i) % cap]! - (s === 'auto' ? mean : 0)
          const x = labelW + (i / (win - 1)) * plotW
          const y = top + rowH / 2 - (v / amp) * (rowH / 2 - 6)
          if (i === 0) ctx.moveTo(x, y)
          else ctx.lineTo(x, y)
        }
        ctx.stroke()
      }
    }
    raf = requestAnimationFrame(draw)
    return () => {
      cancelAnimationFrame(raf)
      unsubscribe()
    }
  }, [channels, bus])

  return (
    <div className="signal-panel">
      <p className="signal-note">
        Live traces. A flat or clipped line is a contact that is lifted or moving. Contact readings below are
        pinned while the signal streams.
      </p>
      <div className="signal-controls">
        <div className="segmented" role="group" aria-label="Y scale">
          {SCALES.map(([v, l]) => (
            <button key={String(v)} type="button" className="segmented__option" aria-pressed={scale === v} onClick={() => setScale(v)}>
              {l}
            </button>
          ))}
        </div>
        <div className="segmented" role="group" aria-label="Time window">
          {WINDOWS.map((v) => (
            <button key={v} type="button" className="segmented__option" aria-pressed={windowSeconds === v} onClick={() => setWindowSeconds(v)}>
              {v} s
            </button>
          ))}
        </div>
        <div className="segmented" role="group" aria-label="Display filter">
          <button
            type="button"
            className="segmented__option"
            aria-pressed={bandpass}
            title="1–40 Hz display bandpass; off = 1 Hz high-pass only"
            onClick={() => setBandpass((b) => !b)}
          >
            1–40 Hz
          </button>
        </div>
      </div>
      <canvas ref={canvasRef} className="signal-canvas" style={{ height: 64 * channels.length }} aria-label="Live signal traces" />
    </div>
  )
}
